import { Box, Button, Divider, Paper, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import FileUpload from 'components/FileUpload'
import { FileFromAPI } from 'components/FileUpload/FileUpload.types'
import { documentExt, imageExt } from 'config/extensions'
import { axiosPost } from 'connectors/axiosPost'
import { useAlert } from 'context/AlertContext'
import { useAuthContext } from 'context/AuthContext'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const imagesFormats = ['webp', 'png', 'jpeg', 'jpg']

export type FormFields = {
  file: File | FileFromAPI | null
}

export type AddDocumentFormProps = {
  prizeGroupId: string
  fileFromAPI: FileFromAPI
}

export const AddDocumentForm = ({ prizeGroupId, fileFromAPI }: AddDocumentFormProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'prize' })
  const { token, refreshToken } = useAuthContext()
  const { changeMessage } = useAlert()
  const queryClient = useQueryClient()

  const { mutate, isLoading } = useMutation(
    async (body: FormData) =>
      await axiosPost<{ affected: number }>(token, refreshToken, `prizes/groups/${prizeGroupId}/document`, body),
    {
      onSuccess: response => {
        if (response !== undefined && !('error' in response)) {
          void queryClient.refetchQueries(['prize-group-data', `prize-${prizeGroupId}`])
          changeMessage('File added', 'success')
        }
      },
    }
  )

  const { handleSubmit, control } = useForm<FormFields>({ defaultValues: { file: fileFromAPI ?? null } })

  const onSubmit = (data: FormFields) => {
    const fd = new FormData()

    if (data.file && !('id' in data.file)) {
      fd.append('file', data.file)

      mutate(fd)
    }
  }

  return (
    <Paper sx={{ p: 4 }}>
      <Typography variant="h6" fontWeight="bold" component="h2">
        {t('emailAttachment')}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FileUpload
          extensions={[...documentExt, ...imageExt]}
          name="file"
          type={fileFromAPI && imagesFormats.includes(fileFromAPI.extension) ? 'image' : 'file'}
          label={t('attachment')}
          control={control}
        />
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {t('save')}
          </Button>
        </Box>
      </form>
    </Paper>
  )
}
